/* eslint-disable no-return-assign */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { INDICATOR_CHANGE } from '../../../../actions/types';
import LegendRow from '../../../../components/LegendRow';
import SelectColoredIcon from '../../../../components/SelectColoredIcon';
import { InputColor, InputText, ListTags } from '../../../../lib/HooksFormFields';
import { defaultColor, mostFrequentInArray } from '../../../../utils/utils';
import { HIERARCHICAL_DONUT } from '../../../../utils/hierarchicalData';

import styles from './Legend.module.scss';

// Helper: sort data items in tree order with depth for hierarchical display
function buildLegendHierarchy(items) {
  if (!items) return [];
  const hasHierarchy = items.some((d) => d.parentName);
  if (!hasHierarchy) return items;
  const childrenMap = {};
  items.forEach((item) => {
    if (item.parentName) {
      if (!childrenMap[item.parentName]) {
        childrenMap[item.parentName] = [];
      }
      childrenMap[item.parentName].push(item);
    }
  });
  function getDepth(item) {
    let depth = 0;
    let current = item;
    while (current?.parentName && items.find((i) => i.name === current.parentName)) {
      depth += 1;
      current = items.find((i) => i.name === current.parentName);
    }
    return depth;
  }
  const roots = items.filter((item) => !item.parentName || !items.find((i) => i.name === item.parentName));
  const result = [];
  function addWithChildren(node) {
    result.push({ ...node, depth: getDepth(node) });
    const children = childrenMap[node.name] || [];
    children.sort((a, b) => (a.name || '').localeCompare(b.name || '')).forEach((child) => addWithChildren(child));
  }
  roots.sort((a, b) => (a.name || '').localeCompare(b.name || '')).forEach((root) => addWithChildren(root));
  items.forEach((item) => {
    if (!result.find((r) => r.name === item.name)) {
      result.push({ ...item, depth: 0 });
    }
  });
  return result;
}

const positions = ['TL', 'TC', 'TR', 'LC', 'RC', 'BL', 'BC', 'BR'];

// graphs able to display the labels on the graph itself
const pieLikeGraphs = ['pie', HIERARCHICAL_DONUT];
const graphsWithLabels = [...pieLikeGraphs, 'histogram'];

const Legend = () => {
  const {
    watch, setValue, control, changedAt,
  } = useOutletContext();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const data = watch('data');
  const legend = watch('config.legend');
  const stacked = watch('config.legend.stacked');

  const itemsRef = useRef([]);

  const handleChangeItem = (value, index) => {
    const items = stacked ? [...stacked] : [...data];
    items[index] = value;
    if (stacked) {
      setValue('config.legend.stacked', [...items]);
    } else {
      setValue('data', [...items]);
    }
    dispatch({
      type: INDICATOR_CHANGE,
    });
  };

  useEffect(() => {
    if (legend?.color && data?.length > 0) {
      const items = data.map((d) => {
        const item = { ...d };
        const sameColor = mostFrequentInArray(data.map((da) => da.color));
        if (item.color === defaultColor
          || sameColor === item.color) {
          item.color = legend.color;
        }
        return item;
      });
      setValue('data', [...items]);
      dispatch({
        type: INDICATOR_CHANGE,
      });
    }
  }, [legend?.color]);

  return (
    <div className={styles.container}>
      <div className={`${styles.legend} ${styles.border}`}>
        <div className={styles.col}>
          <ListTags
            name="config.legend.isActive"
            control={control}
            label={t('indicator.legend.display.toggle')}
            tags={[
              { label: t('options.yes'), value: true },
              { label: t('options.no'), value: false },
            ]}
          />
        </div>
        <div className={styles.col}>
          <ListTags
            name="config.legend.display"
            control={control}
            label="Type d'affichage"
            tags={[
              { label: t('indicator.legend.display.hover'), value: 'hover' },
              { label: t('indicator.legend.display.click'), value: 'click' },
              { label: t('indicator.legend.display.always'), value: 'already' },
            ]}
          />
        </div>
        <div className={styles.color}>
          <label>{t('indicator.legend.icon')}</label>
          {watch('config.legend') && <SelectColoredIcon name='config.legend.icon' control={control} />}
        </div>
      </div>
      {graphsWithLabels.includes(watch('graph')) && watch('config.params.value')?.value !== 'COUNT_RELATIVE'
        && <div className={`${styles.legend} ${styles.border}`}>
          {pieLikeGraphs.includes(watch('graph'))
            && <div className={styles.col} style={{ width: '50%' }}>
                <ListTags
                  name="config.legend.isLabelOnGraph"
                  control={control}
                  label={t('indicator.legend.display.label')}
                  tags={[
                    { label: t('options.yes'), value: true },
                    { label: t('options.no'), value: false },
                  ]}
                />
              </div>
          }
          {graphsWithLabels.includes(watch('graph')) && watch('config.params.value')?.value !== 'COUNT_RELATIVE'
            && <div className={styles.col} style={{ width: '50%' }}>
              <ListTags
                name="config.legend.isValueOnLegend"
                control={control}
                label={t('indicator.legend.display.value')}
                tags={[
                  { label: t('options.yes'), value: true },
                  { label: t('options.no'), value: false },
                ]}
              />
            </div>
          }
        </div>
      }

      <div className={styles.border}>
        <InputText
          name="config.legend.title"
          control={control}
          label={t('indicator.legend.title')}
          placeholder={t('indicator.legend.placeholder')}
        />
        <div className={styles.positions}>
          <label>{t('indicator.legend.position')}</label>
          <div className={styles.options}>
            {positions.map((pos) => <button
              key={pos}
              onClick={() => setValue('config.legend.titlePosition', pos)}
              className={`${styles[pos] ? styles[pos] : ''} ${watch('config.legend.titlePosition') === pos ? styles.active : ''}`}
              >
              {t(`indicator.legend.positions.${pos}`)}
            </button>)}
          </div>
        </div>
      </div>

      {(watch('graph') === 'lines')
        && <div className={`${styles.legend} ${styles.border}`}>
          <div className={styles.col} style={{ width: '50%' }}>
            <InputColor
              name="config.legend.color"
              control={control}
              label={t('indicator.legend.lineColor')}
            />
          </div>
        </div>
      }

      <div className={styles.list}>
        <div className={`${styles.row} ${styles.label}`}>
          <div className={styles.col}>
            <p>{t('indicator.legend.name')}</p>
          </div>
          <div className={styles.col}>
            <p>{t('indicator.legend.color')}</p>
          </div>
          <div className={styles.col}>
            <p>{t('indicator.legend.active')}</p>
          </div>
        </div>
        {!stacked && buildLegendHierarchy(data)?.map((item, i) => {
          const originalIndex = data?.findIndex((d) => d.id === item.id && d.name === item.name);
          return <div key={i + changedAt} style={item.depth ? { paddingLeft: `${item.depth * 16}px` } : {}}>
            {item.depth > 0 && <span className={styles.hierarchyIndent}>└</span>}
            <LegendRow
              handleChange={(value) => handleChangeItem(value, originalIndex >= 0 ? originalIndex : i)}
              item={item}
              ref={itemsRef.current[i]}
              styles={styles}
            />
          </div>;
        })}
        {stacked && buildLegendHierarchy(stacked)?.map((item, i) => {
          const originalIndex = stacked?.findIndex((d) => d.name === item.name);
          return <div key={i + changedAt} style={item.depth ? { paddingLeft: `${item.depth * 16}px` } : {}}>
            {item.depth > 0 && <span className={styles.hierarchyIndent}>└</span>}
            <LegendRow
              handleChange={(value) => handleChangeItem(value, originalIndex >= 0 ? originalIndex : i)}
              item={item}
              ref={itemsRef.current[i]}
              styles={styles}
            />
          </div>;
        })}
      </div>
    </div>
  );
};

export default Legend;
